import { useState, useEffect } from "react";
import { apiFetch } from "../api";
import { theme } from "../theme";
import { fmt, positionColor, positionBg } from "../utils";

// ── Detail Modal ───────────────────────────────────────────────────────────────
export default function DetailModal({ product, token, onClose, groups = [], onGroupChange, vychoziHranice }) {
  const [tab, setTab] = useState("overview");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(product.notify_threshold ?? "");
  const [thresholdSaved, setThresholdSaved] = useState(false);
  const [thresholdError, setThresholdError] = useState("");
  const [watching, setWatching] = useState(product.notify_on_change !== false);
  const [groupId, setGroupId] = useState(product.group_id ?? "");
  const [groupSaving, setGroupSaving] = useState(false);

  const saveGroup = async (hodnota) => {
    const nove = hodnota === "" ? null : Number(hodnota);
    const puvodni = groupId;
    setGroupId(hodnota);
    setGroupSaving(true);
    try {
      await apiFetch("/products/" + product.id + "/group", token, {
        method: "PATCH",
        body: JSON.stringify({ group_id: nove }),
      });
      product.group_id = nove;
      onGroupChange && onGroupChange(product.id, nove);
    } catch (e) {
      setGroupId(puvodni);   // vrátit výběr, když se to neuložilo
    }
    setGroupSaving(false);
  };

  const saveThreshold = async () => {
    const val = threshold === "" ? null : parseInt(threshold);
    setThresholdError("");
    try {
      // Endpoint vrací uloženou hodnotu, takže se řídíme jí, ne tím, co jsme poslali.
      const saved = await apiFetch("/products/" + product.id, token, { method: "PATCH", body: JSON.stringify({ notify_threshold: val }) });
      product.notify_threshold = saved.notify_threshold;
      setThreshold(saved.notify_threshold ?? "");
      setThresholdSaved(true);
      setTimeout(() => setThresholdSaved(false), 2000);
    } catch(e) {
      setThresholdError("Uložení se nepodařilo, zkuste to znovu.");
    }
  };

  const toggleWatching = async () => {
    const next = !watching;
    setWatching(next);
    try {
      await apiFetch("/products/" + product.id + "/notifications", token, { method: "PATCH", body: JSON.stringify({ notify_on_change: next }) });
      product.notify_on_change = next;
    } catch(e) {
      setWatching(!next);   // vrátit přepínač, když se to neuložilo
    }
  };

  useEffect(() => {
    apiFetch(`/products/${product.id}`, token)
      .then(setDetail)
      .catch(() => setDetail(product))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  const data = detail || product;
  const history = data.history || [];

  const w = 500, h = 130, pad = 16;
  const positions = history.map(h => h.our_position).filter(Boolean);
  const min = positions.length ? Math.min(...positions) : 1;
  const max = positions.length ? Math.max(...positions) : 10;
  const range = max - min || 1;
  const pts = positions.map((p, i) => {
    const x = pad + (i / Math.max(positions.length - 1, 1)) * (w - pad * 2);
    const y = pad + ((p - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  }).join(" ");
  const fillPts = positions.length ? `${pad},${h} ${pts} ${w - pad},${h}` : "";

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "#00000040",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 100, padding: 24, backdropFilter: "blur(6px)",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.bg, borderRadius: theme.rCard, width: "100%", maxWidth: 560,
        maxHeight: "88vh", overflow: "auto",
        boxShadow: "0 24px 80px #00000020",
        fontFamily: theme.font,
      }}>
        <div style={{ padding: "24px 28px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
            <div>
              <h2 style={{ color: theme.ink, fontSize: 17, fontWeight: 700, lineHeight: 1.3, marginBottom: 5 }}>
                {data.name}
              </h2>
              <p style={{ color: theme.mut, fontSize: 12, fontVariantNumeric: theme.tnum }}>
                Heureka ID: {data.heureka_product_id}
              </p>
            </div>
            <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: theme.rBtn, border: `1.5px solid ${theme.line}`, background: theme.bg, color: theme.inkSoft, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}><span style={{position:"relative",top:"-1px"}}>✕</span></button>
          </div>

          <div style={{ display: "flex", gap: 6, borderBottom: `1.5px solid ${theme.line}` }}>
            {["overview", "history"].map(t => (
              <button key={t} onClick={() => setTab(t)} style={{
                padding: "9px 18px", border: "none",
                borderBottom: tab === t ? `2.5px solid ${theme.blue}` : "2.5px solid transparent",
                background: "transparent", color: tab === t ? theme.blue : theme.inkSoft,
                fontSize: 14, fontWeight: tab === t ? 700 : 500,
                marginBottom: -1.5, fontFamily: theme.font,
              }}>
                {t === "overview" ? "Přehled" : "Historie"}
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding: 28 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: theme.mut }}>Načítám…</div>
          ) : tab === "overview" ? (
            <>
              <div style={{
                background: `linear-gradient(135deg, ${theme.blueSoft}, #fff)`,
                border: `1.5px solid ${theme.blueLine}`, borderRadius: theme.rCard,
                padding: 20, marginBottom: 20, display: "flex", gap: 20, alignItems: "center",
              }}>
                <div style={{
                  width: 64, height: 64, borderRadius: theme.rTile,
                  background: positionBg(data.position), flexShrink: 0,
                  border: `2px solid ${positionColor(data.position)}30`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <span style={{ color: positionColor(data.position), fontVariantNumeric: theme.tnum, fontWeight: 800, fontSize: 28 }}>
                    {data.position || "—"}
                  </span>
                </div>
                <div>
                  <p style={{ color: theme.inkSoft, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Aktuální pozice</p>
                  <p style={{ color: theme.ink, fontWeight: 700, fontSize: 22, fontVariantNumeric: theme.tnum }}>{fmt(data.our_total_price)}</p>
                  <p style={{ color: theme.mut, fontSize: 13 }}>z <span style={{ fontVariantNumeric: theme.tnum }}>{data.total_offers || "?"}</span> nabídek celkem</p>
                </div>
              </div>

              <div style={{ background: theme.bgSoft, border: `1.5px solid ${theme.line}`, borderRadius: theme.rCard, padding: "16px 18px", marginBottom: 20 }}>
                {/* skupina */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: theme.inkSoft, textTransform: "uppercase", letterSpacing: "0.07em", flex: 1, minWidth: 160 }}>Skupina</span>
                  <select
                    value={groupId}
                    onChange={e => saveGroup(e.target.value)}
                    disabled={groupSaving}
                    style={{ minWidth: 150, padding: "7px 10px", borderRadius: 8, border: `1.5px solid ${theme.line}`, fontSize: 13.5, fontWeight: 600, color: theme.ink, background: theme.bg, fontFamily: "inherit", cursor: "pointer", outline: "none" }}
                  >
                    <option value="">Bez skupiny</option>
                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>

                <div style={{ borderTop: `1px solid ${theme.line}`, margin: "14px 0" }} />

                {/* vypínač hlídání */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: theme.inkSoft, textTransform: "uppercase", letterSpacing: "0.07em", flex: 1, minWidth: 160 }}>Hlídat tento produkt</span>
                  <button onClick={toggleWatching} role="switch" aria-checked={watching} style={{
                    width: 44, height: 25, borderRadius: theme.rBtn, border: "none", padding: 3, cursor: "pointer",
                    background: watching ? theme.blue : theme.line, transition: "background .15s",
                    display: "flex", justifyContent: watching ? "flex-end" : "flex-start", alignItems: "center",
                  }}>
                    <span style={{ width: 19, height: 19, borderRadius: "50%", background: "#fff", display: "block" }} />
                  </button>
                </div>

                {watching && (
                  <>
                    <div style={{ borderTop: `1px solid ${theme.line}`, margin: "14px 0" }} />
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: theme.inkSoft, textTransform: "uppercase", letterSpacing: "0.07em", flex: 1, minWidth: 160 }}>Hlídat pozici do</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, color: theme.mut }}>#</span>
                        <input
                          type="number" min="1" max="50"
                          value={threshold}
                          onChange={e => { setThreshold(e.target.value); setThresholdError(""); }}
                          placeholder={vychoziHranice != null ? String(vychoziHranice) : "výchozí"}
                          style={{ width: 96, padding: "6px 10px", borderRadius: 8, border: `1.5px solid ${thresholdError ? theme.badLine : theme.line}`, fontSize: 14, fontWeight: 600, fontFamily: theme.font, fontVariantNumeric: theme.tnum, textAlign: "center", outline: "none" }}
                        />
                        <button onClick={saveThreshold} style={{ background: thresholdSaved ? theme.ok : theme.blue, color: "#fff", border: "none", borderRadius: theme.rBtn, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "background 0.2s" }}>
                          {thresholdSaved ? "✓ Uloženo" : "Uložit"}
                        </button>
                      </div>
                    </div>
                    <p style={{ margin: "10px 0 0", fontSize: 12, color: theme.mut, lineHeight: 1.5 }}>
                      Ozveme se, když produkt klesne uvnitř této hranice nebo z ní vypadne.
                    </p>
                    <p style={{ margin: "6px 0 0", fontSize: 12, lineHeight: 1.5, color: threshold === "" ? theme.blueDark : theme.mut }}>
                      {threshold === ""
                        ? <>Teď platí výchozí hranice z Nastavení{vychoziHranice != null && <> — <strong style={{ fontVariantNumeric: theme.tnum }}>#{vychoziHranice}</strong></>}. Vyplněním ji pro tenhle produkt přebijete.</>
                        : <>Tenhle produkt má vlastní hranici. Smazáním pole se vrátí k výchozí{vychoziHranice != null && <> <strong style={{ fontVariantNumeric: theme.tnum }}>#{vychoziHranice}</strong></>}.</>}
                    </p>
                    {thresholdError && <p style={{ margin: "8px 0 0", fontSize: 12, color: theme.bad }}>{thresholdError}</p>}
                  </>
                )}
              </div>

              <p style={{ color: theme.inkSoft, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Žebříček — všechny shopy</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {data.raw_shops_json && (() => {
                  const shops = JSON.parse(data.raw_shops_json);
                  return shops.map((shop, i) => {
                    const offer = (shop.offers || []).reduce((best, o) => !best || parseFloat(o.price) < parseFloat(best.price) ? o : best, null);
                    const price = offer ? parseFloat(offer.price) : 0;
                    const delivery = offer ? parseFloat(offer.min_delivery_price || 0) : 0;
                    const total = price + delivery;
                    // Bez nastaveného e-shopu nevíme, který řádek je uživatelův — dřív tu byl
                    // natvrdo cizí shop 47388, takže se cizí obchod označil jako "Váš e-shop".
                    const isOurs = !!data.our_shop_id && String(shop.id) === String(data.our_shop_id);
                    return (
                      <div key={shop.id} style={{
                        background: isOurs ? theme.blueSoft : i === 0 ? theme.okSoft : theme.bgSoft,
                        border: isOurs ? `2px solid ${theme.blueLine}` : i === 0 ? `1.5px solid ${theme.okLine}` : `1.5px solid ${theme.line}`,
                        borderRadius: 10, padding: "12px 16px",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{
                            width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                            background: isOurs ? theme.blue : i === 0 ? theme.ok : theme.line,
                            color: isOurs || i === 0 ? "#fff" : theme.inkSoft,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: 700, fontVariantNumeric: theme.tnum,
                          }}>{i + 1}</span>
                          <span style={{ fontWeight: isOurs ? 700 : 500, fontSize: 14, color: isOurs ? theme.blue : theme.ink }}>
                            {isOurs ? "Váš e-shop" : shop.name}
                          </span>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <span style={{ fontWeight: 700, fontSize: 15, color: isOurs ? theme.blue : theme.ink, fontVariantNumeric: theme.tnum }}>
                            {fmt(total)}
                          </span>
                          {delivery > 0 && <p style={{ fontSize: 11, color: theme.mut, fontVariantNumeric: theme.tnum }}>{fmt(price)} + {fmt(delivery)} dop.</p>}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </>
          ) : (
            <>
              <p style={{ color: theme.inkSoft, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16 }}>
                Vývoj pozice — posledních {history.length} záznamů
              </p>
              {positions.length > 1 ? (
                <div style={{ background: theme.bgSoft, border: `1.5px solid ${theme.line}`, borderRadius: theme.rCard, padding: 16, marginBottom: 20, overflow: "hidden" }}>
                  <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: "block" }}>
                    <defs>
                      <linearGradient id="fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={theme.blue} stopOpacity="0.15" />
                        <stop offset="100%" stopColor={theme.blue} stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    {fillPts && <polygon points={fillPts} fill="url(#fill)" />}
                    <polyline points={pts} fill="none" stroke={theme.blue} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    {positions.map((p, i) => {
                      const x = pad + (i / Math.max(positions.length - 1, 1)) * (w - pad * 2);
                      const y = pad + ((p - min) / range) * (h - pad * 2);
                      return <circle key={i} cx={x} cy={y} r="4.5" fill={theme.blue} stroke="#fff" strokeWidth="2" />;
                    })}
                  </svg>
                </div>
              ) : (
                <div style={{ background: theme.bgSoft, borderRadius: theme.rCard, padding: 24, marginBottom: 20, textAlign: "center", color: theme.mut, fontSize: 13 }}>
                  Zatím málo dat pro zobrazení grafu
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {[...history].reverse().map((snap, i) => (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 14px", borderRadius: 10,
                    background: i === 0 ? theme.blueSoft : theme.bgSoft,
                    border: "1.5px solid " + (i === 0 ? theme.blueLine : theme.line),
                  }}>
                    <span style={{ color: theme.inkSoft, fontSize: 13, fontVariantNumeric: theme.tnum }}>
                      {snap.checked_at}
                    </span>
                    <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
                      <span style={{ color: positionColor(snap.our_position), fontVariantNumeric: theme.tnum, fontWeight: 700, fontSize: 15 }}>
                        #{snap.our_position || "—"}
                      </span>
                      <span style={{ color: theme.inkSoft, fontVariantNumeric: theme.tnum, fontSize: 13 }}>
                        {fmt(snap.our_total_price)}
                      </span>
                    </div>
                  </div>
                ))}
                {history.length === 0 && (
                  <p style={{ color: theme.mut, fontSize: 13, textAlign: "center", padding: 20 }}>Žádná historie zatím</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
